import { useRef } from 'react'

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import {
  CounterBadge,
  IconChevronLeft,
  IconChevronRight,
  IconFileMagnifyingGlass,
  IconSpin
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { Trans } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { SearchResultTile } from './SearchResultTile'
import { SEARCH_URL, searchTabs } from '~/portal/components/SearchModal/constants'
import { useTabScroll } from '~/portal/hooks/useTabScroll'
import emptySearchResultImageDarkDesktop from '~/portal/images/consumer/search/empty-search-results-dark-desktop.svg'
import emptySearchResultImageDarkMobile from '~/portal/images/consumer/search/empty-search-results-dark-mobile.svg'
import emptySearchResultsImage from '~/portal/images/consumer/search/empty-search-results.svg'
import appDefaults from '~/portal/portal.defaults.json'
import { SEARCH_TAB_TYPE, SearchNoOfElements, SearchResult } from '~/portal/types/search'
import { SearchTab } from '~/portal/types/ui.components'
import { useDarkMode } from '~/shared/components/DarkMode/hook'
import { useInfiniteScroll } from '~/shared/hooks/useInfiniteScroll.ts'

const NoResults = () => {
  const { isDark } = useDarkMode()
  const isDesktop = useMediaQuery(appDefaults.media.desktop, { initializeWithValue: false, defaultValue: true })
  const darkModeImage = isDesktop ? emptySearchResultImageDarkDesktop : emptySearchResultImageDarkMobile

  return (
    <div className="sp-search-no-results flex flex-col items-center pt-20 font-medium text-(--sp-color-text)">
      <span className="pb-1 text-body1">
        <Trans i18nKey="components.ui.searchModal.noResults.title" />
      </span>
      <span className="pb-10 text-body3">
        <Trans i18nKey="components.ui.searchModal.noResults.description" />
      </span>
      <img src={isDark() ? darkModeImage : emptySearchResultsImage} alt="no results" width={224.7} height={140} />
    </div>
  )
}

const EmptySearch = () => (
  <div className="sp-search-empty-state flex flex-col items-center pt-30">
    <IconFileMagnifyingGlass />
    <span className="pt-6 font-medium text-body2 text-(--sp-color-text-muted)">
      <Trans i18nKey="components.ui.searchModal.empty" />
    </span>
  </div>
)

const TabListContent = ({
  tabs, noOfElements, onTabChange, selectedTab
} : {
  tabs: SearchTab[], noOfElements: SearchNoOfElements, onTabChange: (type: SEARCH_TAB_TYPE) => void, selectedTab: SEARCH_TAB_TYPE
}) => {
  const getCount = (type: SEARCH_TAB_TYPE) => {
    const elements = {
      [SEARCH_TAB_TYPE.DOCS]: noOfElements.documents || 0,
      [SEARCH_TAB_TYPE.APIS]: noOfElements.apis || 0,
      [SEARCH_TAB_TYPE.OPERATIONS]: noOfElements.operations || 0,
      [SEARCH_TAB_TYPE.SCHEMAS]: noOfElements.schemas || 0,
    }
    if (type === SEARCH_TAB_TYPE.ALL) {
      return elements[SEARCH_TAB_TYPE.DOCS] + elements[SEARCH_TAB_TYPE.APIS] + elements[SEARCH_TAB_TYPE.OPERATIONS] + elements[SEARCH_TAB_TYPE.SCHEMAS]
    }
    return elements[type]
  }

  return (
    <>
      {tabs.map((tab: SearchTab) => (
        <Tab key={tab.type} className="relative flex-1 text-center border-b row border-(--sp-color-border-subtle) pb-2.25 md:pb-1.25" onClick={() => onTabChange(tab.type)}>
          {() => (
            <div className={clsx(
              'flex items-center justify-center px-3.5',
              'pt-3 pb-2.5 -mb-2.5 md:pt-2 md:pb-1.5 md:-mb-1.5',
              selectedTab === tab.type ? 'border-b-2 border-(--sp-color-text)' : 'border-b-2 border-transparent'
            )}>
              <span className={clsx('mr-2.5 text-body2 font-medium', selectedTab === tab.type ? 'text-(--sp-color-text)' : ' text-(--sp-color-text-muted)')}>
                {tab.title}
              </span>
              <span className="relative z-10">
                <CounterBadge counter={getCount(tab.type)} className="sp-search-modal-tab-badge" />
              </span>
            </div>
          )}
        </Tab>
      ))}
    </>
  )
}

interface SearchResultsProps {
  results: SearchResult[]
  totalElements: number
  inputLength: number
  minQueryLength: number
  onClose: () => void
  isLoading?: boolean
  isMobile?: boolean
  onTabChange: (type: SEARCH_TAB_TYPE) => void
  noOfElements: SearchNoOfElements
  searchParams?: Record<string, string>
  selectedTab: SEARCH_TAB_TYPE
}

export const SearchResults = ({
  results,
  totalElements,
  isLoading = false,
  inputLength,
  minQueryLength,
  onClose,
  isMobile = false,
  onTabChange,
  noOfElements,
  searchParams,
  selectedTab,
}: SearchResultsProps) => {
  const { tabListRef, canScrollLeft, canScrollRight, shouldShowArrows } = useTabScroll(searchTabs)

  const infiniteScrollContainerRef = useRef<HTMLDivElement>(null)

  const { data, isFetching, fetchMore } = useInfiniteScroll<SearchResult & { id?: string }>(
    SEARCH_URL,
    results ?? [],
    totalElements,
    infiniteScrollContainerRef.current,
    { searchParams },
  )

  return (
    <TabGroup className="sp-search-modal-tabs">
      {!shouldShowArrows() ?
        <TabList ref={tabListRef} className="flex w-full">
          <TabListContent tabs={searchTabs} noOfElements={noOfElements} onTabChange={onTabChange} selectedTab={selectedTab} />
        </TabList> :
        <div className="relative flex">
          {canScrollLeft && (
            <span className="absolute -left-px flex h-full">
              <span className="sp-search-modal-tab-scroll-arrow flex items-center h-full px-1 bg-(--sp-color-surface) text-(--sp-color-text-muted)"><IconChevronLeft /></span>
              <span className="w-4 h-full bg-linear-to-r from-white to-transparent dark:bg-linear-to-l dark:from-transparent dark:to-neutral-98" />
            </span>
          )}
          <TabList ref={tabListRef} className="flex w-full overflow-x-scroll scrollbar-hide">
            <TabListContent tabs={searchTabs} noOfElements={noOfElements} onTabChange={onTabChange} selectedTab={selectedTab} />
          </TabList>
          {canScrollRight && (
            <span className="absolute -right-px flex h-full">
              <span className="w-4 h-full bg-linear-to-l from-white to-transparent dark:from-transparent dark:to-neutral-98 dark:bg-linear-to-r" />
              <span className="sp-search-modal-tab-scroll-arrow flex items-center h-full px-1 bg-(--sp-color-surface) text-(--sp-color-text-muted)"><IconChevronRight /></span>
            </span>
          )}
        </div>
      }
      <TabPanels>
        {searchTabs.map(tab => (
          <TabPanel
            key={tab.type}
            ref={infiniteScrollContainerRef}
            onScroll={({ target }) => fetchMore(target as HTMLDivElement)}
            className={clsx(
              'overflow-y-auto',
              !isMobile && inputLength >= minQueryLength && data.length && 'border-b border-(--sp-color-border-subtle)',
              !isMobile ? ' h-104' : 'h-[calc(100vh-16.25rem)] md:h-[calc(100vh-15.75rem)]'
            )}
          >
            {inputLength < minQueryLength ?
              <EmptySearch /> :
              (!isLoading && data.length === 0 ?
                <NoResults /> :
                <ul className="w-full pt-1">
                  {data.map((result, i) => ( // eslint-disable-next-line react/no-array-index-key
                    <li key={`${result.title}-${i}`}>
                      <SearchResultTile result={result} onClose={onClose} tileKey={`${result.title}-${i}`} />
                    </li>)
                  )}
                </ul>)}
            {isFetching && (
              <div className="sp-search-loading flex items-center justify-center pt-2 pb-4">
                <IconSpin width={isMobile ? 36 : 48} height={isMobile ? 36 : 48} className="mx-auto text-(--sp-color-link) dark:text-(--sp-color-link)" />
              </div>
            )}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  )
}